import { Box } from '@mui/material';
import { lazy, Suspense, useEffect } from 'react';
import type { CohortHistoryChartConfig, DataPoint } from '@/features/chartsView';
import {
  LineChartWrapper,
  TABLEAU_20_COLORS,
  useLineChartParams,
} from '@/features/chartsView';
import { useDataFetcher } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { isDateDimension, isMediaDimension } from '@/shared/types';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import { formatSnapshotDttm } from '../../model/formatSnapshotDttm';
import { selectMeta } from '../../model/slice';
import type { CohortHistoryChartDataResponse } from '../../model/types';

const SeriesChart = lazy(() =>
  import('@/features/chartsView').then(({ SeriesChart }) => {
    return {
      default: SeriesChart<typeof CHART_BASE_DIMENSION>,
    };
  }),
);

interface Props {
  config: CohortHistoryChartConfig;
  onGetData: () => Promise<CohortHistoryChartDataResponse>;
  onDataChange?: (data?: CohortHistoryChartDataResponse) => void;
}

export function Chart({ config, onGetData, onDataChange }: Props) {
  const historyMeta = useAppSelector(selectMeta);
  const isMediaContent = historyMeta
    ? isMediaDimension(historyMeta.config, config.splitDimension)
    : false;
  const isDateContent = historyMeta
    ? isDateDimension(historyMeta.config, config.splitDimension)
    : false;
  const { data, dataStatus } = useDataFetcher(onGetData);
  const splitOptions = data?.splitOptions?.[config.splitDimension] || [];
  const {
    noData,
    lines,
    leftY,
    rightY,
    data: chartData,
  } = useLineChartParams({
    meta: historyMeta,
    chart: config,
    data: (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
    splitOptions,
    defaultSplitOption: CHART_BASE_DIMENSION,
    excludedSplitOptions: config.excludedSplitOptions,
  });

  useEffect(() => {
    onDataChange?.(data);
  }, [data, onDataChange]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LineChartWrapper
        dataStatus={dataStatus}
        noData={noData}
        noSelection={lines.length === config.excludedSplitOptions.length}
      >
        <Suspense fallback={<Loader active />}>
          <SeriesChart
            sx={{ border: 'none' }}
            syncYAxesDomains={config.syncedYAxes}
            excludedLines={config.excludedSplitOptions}
            isMediaContent={isMediaContent}
            isDateContent={isDateContent}
            xAxisDataKey={CHART_BASE_DIMENSION}
            xAxisFormatter={formatSnapshotDttm}
            leftY={leftY}
            rightY={rightY}
            data={chartData}
            lines={lines}
            leftSeries={config.leftAxisSeries}
            rightSeries={config.rightAxisSeries}
            palette={TABLEAU_20_COLORS}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
